import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { change, Field, reduxForm } from 'redux-form';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import {
  clearTollItineraryLiquidation,
  clearTollItineraryLiquidationDetail,
  getTollItineraryLiquidation,
  getTollItineraryLiquidationDetail,
} from '../../../../actions/route/TollLiquidation';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import { isRequired } from '../../../../utils/validators';
import TollLiquidationItinerarySearchForm from './TollLiquidationItinerarySearchForm';
import Table from '../../../common/Table';
import { TOLL_ITINERARY_LIQUIDATION_COLUMNS } from '../../../../config/columns';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import Loader from '../../../common/Loader';
import TollSelectorTable from './TollSelectorTable';
import AdditionalChargeSelectorTable from './AdditionalChargeSelectorTable';
import FormFooter from '../../../common/forms/FormFooter';
import TollLiquidationDifferenceReasonSelect from '../../../common/forms/select/TollLiquidationDifferenceReasonSelect';
import { numberFormatter } from '../../../../utils/number';

const createInitialTollSelection = () => ({
  selectedRows: [],
  globalTotal: 0,
  selectedTotal: 0,
  global: {
    peaje: 0,
    detraction: 0,
  },
  selected: {
    peaje: 0,
    detraction: 0,
  },
});

const createInitialAdditionalSelection = () => ({
  selectedRows: [],
  globalTotal: 0,
  selectedTotal: 0,
});

const roundAmount = (amount) => Math.round(amount * 100) / 100;

const TollLiquidationForm = ({
  handleSubmit,
  onSubmit,
  dispatchGetTollItineraryLiquidation,
  dispatchClearTollItineraryLiquidation,
  dispatchGetTollItineraryLiquidationDetail,
  dispatchClearTollItineraryLiquidationDetail,
  itineraryTollLiquidations,
  itineraryTollLiquidationDetail,
  dispatchChange,
  loading,
  loadingDetail,
}) => {
  useLayoutEffect(
    () => () => {
      dispatchClearTollItineraryLiquidationDetail();
      dispatchClearTollItineraryLiquidation();
    },
    [],
  );

  const [selectedItinerary, setSelectedItinerary] = useState();

  const [selectedRegisteredBus, setSelectedRegisteredBus] = useState();

  const [selectedLiquidatorDriver, setSelectedLiquidatorDriver] = useState();

  const [lastSearchParams, setLastSearchParams] = useState(null);

  const [tollSelection, setTollSelection] = useState(
    createInitialTollSelection,
  );

  const [additionalSelection, setAdditionalSelection] = useState(
    createInitialAdditionalSelection,
  );

  const expectedAmountToDeposit = useMemo(() => {
    const tollTotal = Number(tollSelection.globalTotal) || 0;
    const additionalTotal = Number(additionalSelection.globalTotal) || 0;

    return roundAmount(tollTotal + additionalTotal);
  }, [tollSelection.globalTotal, additionalSelection.globalTotal]);

  const amountDeposited = useMemo(() => {
    const tollTotal = Number(tollSelection.selectedTotal) || 0;
    const additionalTotal = Number(additionalSelection.selectedTotal) || 0;

    return roundAmount(tollTotal + additionalTotal);
  }, [tollSelection.selectedTotal, additionalSelection.selectedTotal]);

  const differenceAmount = useMemo(() => {
    const difference = expectedAmountToDeposit - amountDeposited;

    return roundAmount(difference);
  }, [expectedAmountToDeposit, amountDeposited]);

  const showLiquidationDifference = useMemo(
    () => Math.abs(differenceAmount) > 0.01,
    [differenceAmount],
  );

  useEffect(() => {
    dispatchChange(
      'TollLiquidationForm',
      'totalAmount',
      expectedAmountToDeposit.toFixed(2),
    );

    dispatchChange(
      'TollLiquidationForm',
      'tollLiquidateAmount',
      amountDeposited.toFixed(2),
    );
  }, [expectedAmountToDeposit, amountDeposited, dispatchChange]);

  useEffect(() => {
    if (!showLiquidationDifference) {
      dispatchChange('TollLiquidationForm', 'differenceAmountReasonId', null);

      dispatchChange('TollLiquidationForm', 'observation', '');
    }
  }, [showLiquidationDifference, dispatchChange]);

  const tollRows = useMemo(() => {
    if (
      !itineraryTollLiquidationDetail ||
      itineraryTollLiquidationDetail.isEmpty()
    ) {
      return [];
    }

    const list = itineraryTollLiquidationDetail.get(
      'itineraryTollDetailDtoList',
      [],
    );

    return list && list.toJS ? list.toJS() : list;
  }, [itineraryTollLiquidationDetail]);

  const additionalRows = useMemo(() => {
    if (
      !itineraryTollLiquidationDetail ||
      itineraryTollLiquidationDetail.isEmpty()
    ) {
      return [];
    }

    const list = itineraryTollLiquidationDetail.get(
      'itineraryTollAdditionalDetailDtoList',
      [],
    );

    return list && list.toJS ? list.toJS() : list;
  }, [itineraryTollLiquidationDetail]);

  const onHandleSubmit = (formValues) => {
    setSelectedRegisteredBus(formValues.registeredBusId.value);

    const newFormValues = {
      companyBusId: formValues.registeredBusId.companyBusId,

      from: tzNormalizeDate({
        date: formValues.fromDateString,
        format: INT_DATE_FORMAT,
      }),

      to: tzNormalizeDate({
        date: formValues.toDateString,
        format: INT_DATE_FORMAT,
      }),
    };

    setLastSearchParams(newFormValues);

    dispatchGetTollItineraryLiquidation(newFormValues);
  };
  const onSelectItinerary = (itinerary) => {
    dispatchChange('TollLiquidationForm', 'totalAmount', 0.0);

    dispatchChange('TollLiquidationForm', 'tollLiquidateAmount', 0.0);

    dispatchChange('TollLiquidationForm', 'differenceAmountReasonId', null);

    dispatchChange('TollLiquidationForm', 'observation', '');

    setTollSelection(createInitialTollSelection());

    setAdditionalSelection(createInitialAdditionalSelection());

    setSelectedLiquidatorDriver(undefined);

    dispatchClearTollItineraryLiquidationDetail();

    setSelectedItinerary(itinerary);

    dispatchGetTollItineraryLiquidationDetail({
      itineraryId: itinerary.itineraryId,
    });
  };

  const onSelectedLiquidatorDriver = (driver) => {
    setSelectedLiquidatorDriver(driver);
  };

  const onTollsChange = (payload) => {
    setTollSelection(payload);
  };

  const onAdditionalChargesChange = (payload) => {
    setAdditionalSelection(payload);
  };

  const hasData =
    itineraryTollLiquidations && itineraryTollLiquidations.size > 0;

  const itineraryTollTable = () => (
    <Table
      manual={false}
      columns={TOLL_ITINERARY_LIQUIDATION_COLUMNS}
      data={itineraryTollLiquidations.toJS()}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      loading={loading}
      highlightSelectedRow
      defaultFilterMethod={filterMethodCaseInsensitive}
      getRowData={onSelectItinerary}
      showPagination
      filterable
      keyField="itineraryId"
    />
  );

  const itineraryLiquidatorDriverTable = () => {
    let rawDriverList = [];

    if (
      itineraryTollLiquidationDetail &&
      itineraryTollLiquidationDetail.get &&
      itineraryTollLiquidationDetail.get('driverList')
    ) {
      rawDriverList = itineraryTollLiquidationDetail.get('driverList');
    }

    let driverListArray = [];

    if (Immutable.List.isList(rawDriverList)) {
      driverListArray = rawDriverList.toJS();
    } else if (Array.isArray(rawDriverList)) {
      driverListArray = rawDriverList;
    }

    const pageSize = driverListArray.length > 0 ? driverListArray.length : 3;

    return (
      <Table
        manual={false}
        columns={[
          {
            Header: 'Nombre',
            accessor: 'fullName',
            className: 'text-center',
          },
          {
            Header: 'DNI',
            accessor: 'idDocumentNumber',
            className: 'text-center',
          },
        ]}
        data={driverListArray}
        defaultPageSize={pageSize}
        showPagination={false}
        loading={loadingDetail}
        highlightSelectedRow
        defaultFilterMethod={filterMethodCaseInsensitive}
        getRowData={onSelectedLiquidatorDriver}
      />
    );
  };

  const onHandleSubmitLiquidation = async (formValues) => {
    const success = await onSubmit(
      formValues,
      selectedItinerary,
      selectedRegisteredBus,
      selectedLiquidatorDriver,
      tollSelection,
      additionalSelection,
      differenceAmount,
      itineraryTollLiquidationDetail.get('payeeCustomer'),
    );

    if (!success) {
      return;
    }

    setSelectedItinerary(undefined);

    setSelectedLiquidatorDriver(undefined);

    setTollSelection(createInitialTollSelection());

    setAdditionalSelection(createInitialAdditionalSelection());

    dispatchClearTollItineraryLiquidationDetail();

    dispatchChange('TollLiquidationForm', 'totalAmount', '0.00');

    dispatchChange('TollLiquidationForm', 'tollLiquidateAmount', '0.00');

    dispatchChange('TollLiquidationForm', 'differenceAmountReasonId', null);

    dispatchChange('TollLiquidationForm', 'observation', '');

    if (lastSearchParams) {
      dispatchGetTollItineraryLiquidation(lastSearchParams);
    }
  };

  return (
    <>
      <h3>Busqueda de Viajes por Bus</h3>

      <TollLiquidationItinerarySearchForm
        onSubmit={onHandleSubmit}
        initialValues={{
          fromDateString: tzNormalizeDate(),
          toDateString: tzNormalizeDate(),
        }}
      />

      <hr />

      {loading && <Loader />}

      {!loading && hasData && itineraryTollTable()}

      {!hasData && (
        <div className="alert alert-info">
          Para ver datos utilice los filtros
        </div>
      )}

      {loadingDetail && <Loader />}

      {itineraryTollLiquidationDetail &&
        !itineraryTollLiquidationDetail.isEmpty() && (
          <>
            <h3 className="mt-4">Información del itinerario</h3>

            <div className="alert alert-secondary">
              <div>
                <strong>Circuito:</strong>{' '}
                {itineraryTollLiquidationDetail.get('circuitName', '-')}
              </div>

              <div>
                <strong>Ruta:</strong>{' '}
                {itineraryTollLiquidationDetail.get('routeName', '-')}
              </div>

              <div>
                <strong>Conductor que recibió:</strong>{' '}
                {itineraryTollLiquidationDetail.get('payeeCustomer')
                  ? `${
                      itineraryTollLiquidationDetail.get('payeeCustomer')
                        .fullName
                    } - ${
                      itineraryTollLiquidationDetail.get('payeeCustomer')
                        .identificationType.name
                    } ${
                      itineraryTollLiquidationDetail.get('payeeCustomer')
                        .idDocumentNumber
                    }`
                  : '-'}
              </div>
            </div>

            <h3 className="mt-4">Conductor Liquidador</h3>

            {itineraryLiquidatorDriverTable()}

            <h3 className="mt-4">Peajes y detracciones</h3>

            <TollSelectorTable rows={tollRows} onChange={onTollsChange} />

            <AdditionalChargeSelectorTable
              rows={additionalRows}
              onChange={onAdditionalChargesChange}
            />
          </>
        )}

      <Form className="mt-3" onSubmit={handleSubmit(onHandleSubmitLiquidation)}>
        <FormGroup row>
          <FormItem label="Total a Liquidar" required>
            <Field
              name="totalAmount"
              component={TextInput}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Monto Liquidado" required>
            <Field
              name="tollLiquidateAmount"
              component={TextInput}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>

        {showLiquidationDifference && (
          <Fragment>
            <h3 className="mt-4">Discrepancia</h3>

            <div className="alert alert-danger">
              <div>
                <strong>Total a Liquidar:</strong>{' '}
                {numberFormatter({
                  value: expectedAmountToDeposit,
                })}
              </div>

              <div>
                <strong>Monto Liquidado:</strong>{' '}
                {numberFormatter({
                  value: amountDeposited,
                })}
              </div>

              <div>
                <strong>Monto de Discrepancia:</strong>{' '}
                {numberFormatter({
                  value: differenceAmount,
                })}
              </div>
            </div>

            <FormGroup row>
              <FormItem label="Razón" required>
                <Field
                  name="differenceAmountReasonId"
                  component={TollLiquidationDifferenceReasonSelect}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Observación">
                <Field
                  name="observation"
                  component={TextInput}
                  type="textarea"
                />
              </FormItem>
            </FormGroup>
          </Fragment>
        )}

        <FormFooter
          saveButtonDisabled={loading || loadingDetail}
          saveButtonIcon={
            loading || loadingDetail ? 'fa fa-spinner fa-spin' : 'fa fa-save'
          }
        />
      </Form>
    </>
  );
};

TollLiquidationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,

  dispatchGetTollItineraryLiquidation: PropTypes.func.isRequired,

  dispatchClearTollItineraryLiquidation: PropTypes.func.isRequired,

  dispatchGetTollItineraryLiquidationDetail: PropTypes.func.isRequired,

  dispatchClearTollItineraryLiquidationDetail: PropTypes.func.isRequired,

  itineraryTollLiquidations: PropTypes.instanceOf(Immutable.List),

  itineraryTollLiquidationDetail: PropTypes.instanceOf(Immutable.Map)
    .isRequired,

  loading: PropTypes.bool.isRequired,

  loadingDetail: PropTypes.bool,

  dispatchChange: PropTypes.func.isRequired,

  onSubmit: PropTypes.func.isRequired,
};

TollLiquidationForm.defaultProps = {
  itineraryTollLiquidations: Immutable.List(),

  loadingDetail: false,
};

const mapDispatchToProps = {
  dispatchGetTollItineraryLiquidation: getTollItineraryLiquidation,

  dispatchClearTollItineraryLiquidation: clearTollItineraryLiquidation,

  dispatchGetTollItineraryLiquidationDetail: getTollItineraryLiquidationDetail,

  dispatchClearTollItineraryLiquidationDetail:
    clearTollItineraryLiquidationDetail,

  dispatchChange: change,
};

const mapStateToProps = ({ RouteUnit }) => ({
  itineraryTollLiquidations: RouteUnit.TollLiquidation.getIn([
    'itineraryToll',
    'content',
  ]),

  loading: !RouteUnit.TollLiquidation.getIn(['current', 'activity']).isEmpty(),

  itineraryTollLiquidationDetail: RouteUnit.TollLiquidation.getIn([
    'itineraryToll',
    'detail',
  ]),

  loadingDetail: !RouteUnit.TollLiquidation.getIn([
    'itineraryToll',
    'activity',
  ]).isEmpty(),
});

const formComponent = reduxForm({
  form: 'TollLiquidationForm',
})(TollLiquidationForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
